import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Coins, Ticket, Home, CheckCircle2, ArrowRight, Sparkles, Tag, Percent, Star, MapPin } from 'lucide-react';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-151" data-mp="118ua6hzk0tvj">
    {children}
  </motion.div>;
export function BairrouClube() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-152" data-mp="1kxcvuplcpp5h">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px'
    }} data-id="element-153" data-mp="11fo60t45iids"></div>

      {/* Hero Section */}
      <section className="px-6 py-16 md:py-24 max-w-7xl mx-auto text-center relative z-10" data-id="element-154" data-mp="mi47o51rflgl">
        <FadeIn data-id="element-155" data-mp="1k5mmnxm0ljt1">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-156" data-mp="1k65v8npryvue">
            <Gift size={18} className="text-brand-yellow" aria-hidden="true" data-id="element-157" data-mp="1f8vzkqbslny4" />
            {t.clube.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-158" data-mp="15h9t17aegd1p">
            {t.clube.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-159" data-mp="1y4vsq4giaxja">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-160" data-mp="1y3ayyh20nimh">
                {t.clube.heroTitle2}
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-161" data-mp="3iny9tej2mxef">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" aria-hidden="true" data-id="element-162" data-mp="39mi8dwa8i5mc" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 font-sans mb-6 leading-relaxed max-w-3xl mx-auto" data-id="element-163" data-mp="34oq566452x2u">
            {t.clube.heroDesc1}
          </p>
          <p className="text-lg text-brand-charcoal/60 dark:text-gray-400 font-sans mb-10 leading-relaxed max-w-3xl mx-auto" data-id="element-164" data-mp="12w234038efon">
            {t.clube.heroDesc2}
          </p>
          <span className="inline-flex items-center gap-2 bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light px-6 py-3 rounded-full text-sm font-bold tracking-wide uppercase border border-brand-green/20 dark:border-brand-green/30 cursor-default select-none" data-id="element-165" data-mp="2nc01z8vhxja4">
            <Sparkles size={16} aria-hidden="true" data-id="element-166" data-mp="2sbl2hnqbdzoy" />
            {t.common.disponivelEmBreve}
          </span>
        </FadeIn>
      </section>

      {/* BairrouPoints */}
      <section className="bg-white dark:bg-gray-800 py-24 px-6 relative z-10 transition-colors duration-300 border-y border-gray-100 dark:border-gray-700" data-id="element-167" data-mp="3157t6zfo0cra">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-168" data-mp="3b3dw9kd7joxq">
          <FadeIn data-id="element-169" data-mp="1loz7x163q42r">
            <span className="inline-flex items-center gap-2 text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4" data-id="element-170" data-mp="1loh8jvo579qm">
              <Coins size={16} className="text-brand-yellow" aria-hidden="true" data-id="element-171" data-mp="1gs8vqoxf3smm" />
              {t.clube.pointsTitle}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-172" data-mp="ctvfrazszini">
              {t.clube.pointsSubtitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-4 leading-relaxed" data-id="element-173" data-mp="lrxko1mnqvg2">
              {t.clube.pointsDesc1}
            </p>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-8 leading-relaxed font-semibold" data-id="element-174" data-mp="3q65k0ixf96h4">
              {t.clube.pointsDesc2}
            </p>
            <p className="text-sm font-bold text-brand-charcoal dark:text-white mb-5 uppercase tracking-wide" data-id="element-175" data-mp="3u6pfli2ck215">
              {t.clube.pointsRedeemIntro}
            </p>
            <ul className="space-y-4" data-id="element-176" data-mp="32gup3i2r66aw">
              {t.clube.pointsList.map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-177" data-mp="36hen0nv5hdy3" data-mpi={i}>
                  <div className="w-8 h-8 rounded-full bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center shrink-0" data-id="element-178" data-mp="2yzyj46littfg">
                    <CheckCircle2 size={18} aria-hidden="true" data-id="element-179" data-mp="2py8vlburfemy" />
                  </div>
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium" data-id="element-180" data-mp="2kzxm528dn659">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
          <FadeIn delay={0.2} className="relative" data-id="element-181" data-mp="2b9cekpaq6ra7">
            <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-182" data-mp="2b8jh6exnpe4q"></div>
            <div className="bg-brand-charcoal dark:bg-gray-950 rounded-3xl p-10 text-center shadow-xl border border-gray-800 dark:border-gray-900" data-id="element-183" data-mp="229ft0wjmu8jd">
              <div className="w-24 h-24 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 transform -rotate-6 shadow-lg" data-id="element-184" data-mp="1x9jo8v342d2r">
                <Coins size={48} className="text-brand-charcoal" aria-hidden="true" data-id="element-185" data-mp="1innqtgwvi186" />
              </div>
              <p className="font-display text-5xl text-brand-yellow mb-2 transform -rotate-1" data-id="element-186" data-mp="1nk4gifjq0nnd">
                BairrouPoints!
              </p>
              <p className="text-white/60 font-medium" data-id="element-187" data-mp="1dnhyrzmoycpk">
                {t.clube.pointsBenefit}
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Cupons e Ofertas */}
      <section className="py-24 px-6 max-w-7xl mx-auto relative z-10" data-id="element-188" data-mp="26bxbugm9iky9">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-189" data-mp="1rolyqaafmsw1">
          <FadeIn delay={0.2} className="order-2 lg:order-1 grid grid-cols-2 gap-5" data-id="element-190" data-mp="1rlzou402k1an">
            {[{
            icon: <Percent size={28} data-id="element-191" data-mp="2kby9zob57ea1" />,
            label: t.clube.cardLabels[0]
          }, {
            icon: <Tag size={28} data-id="element-192" data-mp="2p4reqc6m2mm4" />,
            label: t.clube.cardLabels[1]
          }, {
            icon: <Star size={28} data-id="element-193" data-mp="dm4pcgk4y3ho" />,
            label: t.clube.cardLabels[2]
          }, {
            icon: <MapPin size={28} data-id="element-194" data-mp="ne0zipzhxk29" />,
            label: t.clube.cardLabels[3]
          }].map((card, i) => <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-195" data-mp="ig907awiuvo1" data-mpi={i}>
                <div className="w-14 h-14 rounded-xl bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center mx-auto mb-3" data-id="element-196" data-mp="3lwy4srcjnvb2">
                  {card.icon}
                </div>
                <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-197" data-mp="3vwgn73ct3910">
                  {card.label}
                </p>
              </div>)}
          </FadeIn>
          <FadeIn className="order-1 lg:order-2" data-id="element-198" data-mp="1b5xz9ipo8ln0">
            <span className="inline-flex items-center gap-2 text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4" data-id="element-199" data-mp="34ndg6fewm0zt">
              <Ticket size={16} className="text-brand-yellow" aria-hidden="true" data-id="element-200" data-mp="39jc8ge3cs35i" />
              {t.clube.couponsTitle}
            </span>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-201" data-mp="3jdbaze7c9oxz">
              {t.clube.couponsSubtitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-4 leading-relaxed" data-id="element-202" data-mp="3se9dtt61jqez">
              {t.clube.couponsDesc1}
            </p>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-203" data-mp="6587ns76vtiv">
              {t.clube.couponsDesc2}
            </p>
            <p className="text-sm font-bold text-brand-charcoal dark:text-white mb-5 uppercase tracking-wide" data-id="element-204" data-mp="22zl3dxh0gijl">
              {t.clube.couponsIntro}
            </p>
            <ul className="grid sm:grid-cols-2 gap-3" data-id="element-205" data-mp="p2099u4u89wb">
              {t.clube.couponsList.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-206" data-mp="k8xb18p48c9s" data-mpi={i}>
                  <CheckCircle2 size={18} className="text-brand-green shrink-0 mt-0.5" aria-hidden="true" data-id="element-207" data-mp="g83rssmmbln8" />
                  <span className="text-brand-charcoal dark:text-gray-200 font-medium text-sm" data-id="element-208" data-mp="180ky1fj9b36s">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* Quanto mais local, melhor */}
      <section className="bg-brand-green-dark text-white py-28 px-6 relative overflow-hidden" data-id="element-209" data-mp="u0a3o2iqn6gv">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-green/30 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-210" data-mp="3jply2vmqsklj"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-211" data-mp="373vxv0njvvgk">
          <FadeIn data-id="element-212" data-mp="3721dpok3s7dj">
            <div className="w-16 h-16 bg-brand-yellow rounded-2xl flex items-center justify-center mx-auto mb-8 transform -rotate-3" data-id="element-213" data-mp="3252ljbk02ahz">
              <Home size={32} className="text-brand-charcoal" aria-hidden="true" data-id="element-214" data-mp="3gv0cu0d87gbe" />
            </div>
            <h2 className="text-4xl md:text-5xl font-sans font-extrabold mb-8 tracking-tight" data-id="element-215" data-mp="3pun25nsokvo3">
              {t.clube.localTitle}
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed" data-id="element-216" data-mp="3kuqy5ojzh48j">
              {t.clube.localDesc}
            </p>
            <p className="text-3xl md:text-4xl font-display text-brand-yellow transform -rotate-1 mb-10" data-id="element-217" data-mp="3mnhkl8h13ny">
              {t.clube.localHighlight}
            </p>
            <p className="text-lg font-bold tracking-wide text-white/90 uppercase" data-id="element-218" data-mp="25e0cchgq5v6a">
              {t.clube.tagline}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center max-w-4xl mx-auto relative z-10" data-id="element-219" data-mp="fypl68etksau">
        <FadeIn data-id="element-220" data-mp="kx96q7pi422o">
          <h2 className="text-5xl md:text-6xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-8 tracking-tight" data-id="element-221" data-mp="kvpt6ov8c54l">
            {t.clube.ctaTitle}
          </h2>
          <p className="text-xl text-brand-charcoal/60 dark:text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-222" data-mp="b6eln6nzr8it">
            {t.clube.ctaDesc}
          </p>
          <Link to="/contato" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-10 py-4 rounded-full text-base font-bold transition-all shadow-sm hover:shadow-md group" data-id="element-223" data-mp="3tenkwl1g1clt">
            {t.clube.ctaButton}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-224" data-mp="3odzspw1hapq0" />
          </Link>
        </FadeIn>
      </section>
    </div>;
}